import React, { useState, ReactNode, ReactElement, useEffect } from "react";
import classNames from "classnames";
import { View } from "@tarojs/components";

import Popup from "../Popup";
import "./index.scss";

export interface ActionSheetOption {
  text: ReactNode;
  value: string | number;
  [key: string]: any;
}

export type ActionValue = string | ActionSheetOption;
export interface ActionProps<T extends ActionValue = ActionValue> {
  visible?: boolean;
  title?: ReactNode;
  data: T[];
  defaultValue?: T;
  className?: string;
  style?: React.CSSProperties;
  onConfirm?: (selectedValue: T, index: number) => void;
  onCancel?: (bottom?: boolean) => void;
  onClose?: () => void;
  onClick?: (flag: boolean) => void;
  children?: ReactNode;
  description?: ReactNode;
  /**
   * @summary controlled模式下，组件为受控组件，需要外部来显示和隐藏
   * @description submit 模式下会显示提交按钮，可以自定义，也可以显示取消按钮但是得传进来一个Toggle事件用来显示隐藏
   */
  mode?: "controlled";
  cancelText?: ReactNode;
  /**
   * submit模式下，默认不展示取消按钮，可以设置
   */
  showCancel?: boolean;
  submitText?: string;
  renderItem?: (item: T, index: number) => ReactNode;
  ItemWrapper?: React.FC<{}>;
  onSubmit?: () => void;
  justifyContent?: "start" | "end";
  /**
   * 显示右上角的关闭、默认只有不显示下面的取消的时候才出现
   */
  showClose?: boolean;
  inline?: boolean;
  dataTrackId?: string;
}

const ActionSheet = <T extends ActionValue>(
  props: ActionProps<T>
): ReactElement => {
  const {
    visible = false,
    title,
    description,
    data = [],
    onConfirm,
    onCancel,
    className,
    children,
    style,
    mode,
    submitText = "提交",
    cancelText = "取消",
    showCancel = false,
    renderItem,
    ItemWrapper,
    onSubmit,
    onClick,
    showClose = false,
    justifyContent = "center",
    inline = false,
    defaultValue = "",
    dataTrackId = "action-sheet",
  } = props;

  const [innerVisible, setInnerVisible] = useState(visible);
  const [value, selectedValue] = useState(
    typeof defaultValue === "string" ? defaultValue : defaultValue?.value
  );

  const handleCancel = (bottom?: boolean) => {
    onCancel?.(bottom);
    onClick?.(false);
    setInnerVisible(false);
  };

  useEffect(() => {
    setInnerVisible(visible);
  }, [visible]);

  const handleClick = () => {
    if (onClick) {
      onClick(!visible);
      return;
    }
    if (data.length <= 0) return;
    setInnerVisible(true);
  };

  const handleConfirm = (item: T, index: number) => {
    onConfirm?.(item, index);
    setInnerVisible(false);
    const checkValue = typeof item === "string" ? item : item.value;
    selectedValue(checkValue);
  };

  const handleSubmit = () => {
    onSubmit?.();
  };

  const submitButton = () => {
    return (
      <View>
        <View
          className="xh-action-sheet-main-action-submit"
          // @ts-ignore
          dataTrackId={`${dataTrackId}-submit`}
          onClick={handleSubmit}
        >
          {submitText}
        </View>
        {showCancel && (
          <View
            className="xh-action-sheet-main-action-cancel mode"
            // @ts-ignore
            dataTrackId={`${dataTrackId}-cancel`}
            onClick={() => handleCancel(true)}
          >
            {cancelText}
          </View>
        )}
      </View>
    );
  };

  const itemRender = () =>
    data.map((item, index) => {
      return renderItem ? (
        renderItem(item, index)
      ) : (
        <View
          // @ts-ignore
          dataTrackId={`${dataTrackId}-item-${index}`}
          className={classNames(
            "xh-action-sheet-main-panel-item",
            justifyContent,
            {
              active:
                typeof item === "string"
                  ? item === value
                  : item.value === value,
            }
          )}
          key={index}
          onClick={() => {
            handleConfirm(item, index);
          }}
        >
          {typeof item === "string" ? item : item.text}
        </View>
      );
    });

  const innerShowClose = showClose || (mode === "controlled" && !showCancel);

  return (
    <View
      className={classNames("xh-action-sheet", { inline })}
      onClick={handleClick}
      // @ts-ignore
      dataTrackId={`${dataTrackId}-wrapper`}
    >
      <Popup visible={innerVisible} onClose={handleCancel}>
        <View
          className={classNames("xh-action-sheet-main", className, mode)}
          style={style}
        >
          {title && (
            <View className="xh-action-sheet-main-header">
              <View className="xh-action-sheet-main-header-title">{title}</View>
              <View className="xh-action-sheet-main-header-desc">
                {description}
              </View>
              {innerShowClose && (
                <View
                  className="xh-action-sheet-main-header-close"
                  onClick={() => handleCancel()}
                ></View>
              )}
            </View>
          )}
          <View
            // @ts-ignore
            dataTrackId={`${dataTrackId}-panel`}
            className={classNames("xh-action-sheet-main-panel", {
              noTitle: !title,
            })}
            onClick={e => process.env.TARO_ENV !== "h5" && e.stopPropagation()}
          >
            {ItemWrapper ? (
              <ItemWrapper>{itemRender()}</ItemWrapper>
            ) : (
              <React.Fragment>{itemRender()}</React.Fragment>
            )}
          </View>
          <View
            // @ts-ignore
            dataTrackId={`${dataTrackId}-action`}
            className={classNames("xh-action-sheet-main-action", {
              noMarinTop: mode !== "controlled" && innerShowClose,
            })}
            onClick={e => process.env.TARO_ENV !== "h5" && e.stopPropagation()}
          >
            {mode === "controlled" ? (
              submitButton()
            ) : !innerShowClose ? (
              <View
                // @ts-ignore
                dataTrackId={`${dataTrackId}-cancel-bottom`}
                className="xh-action-sheet-main-action-cancel"
                onClick={() => handleCancel(true)}
              >
                {cancelText}
              </View>
            ) : (
              <div></div>
            )}
          </View>
        </View>
      </Popup>
      {children}
    </View>
  );
};

export default ActionSheet;
